'use client'

import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/ui/password-input'
import { Button } from '@/components/ui/button'
import { login, signup, signInWithLinkedIn } from './actions'
import { RECOVERY_LINK_FAILED_CODE, MAGIC_LINK_FAILED_CODE } from '@/lib/utils/auth-urls'
import { toast } from 'sonner'
import { Loader2, Linkedin } from 'lucide-react'
import { useKeyboardVisible } from '@/lib/hooks/use-keyboard-visible'
import { useLocale } from 'next-intl'
import { LocaleSwitcher } from '@/components/locale-switcher'

interface LoginFormProps {
  organization?: {
    name: string
    logo_url: string | null
    slug: string
  } | null
  referralCode?: string
  referrerName?: string
  initialFlow?: 'signup'
}

export function LoginForm({
  organization,
  referralCode,
  referrerName,
  initialFlow,
}: LoginFormProps) {
  const t = useTranslations('auth')
  const [isLoading, setIsLoading] = useState(false)
  const [isLinkedInLoading, setIsLinkedInLoading] = useState(false)
  const searchParams = useSearchParams()

  // The OAuth callback path still emits a localized `?error=` string for
  // legacy reasons; the token-hash confirm route emits `?errorCode=` only
  // and we translate client-side from a single source of truth. That avoids
  // the SSR/CSR locale split where server-rendered text would land in a UI
  // rendered under a different cookie locale.
  const errorParam = searchParams.get('error')
  const errorCode = searchParams.get('errorCode')
  const isRecoveryLinkFailed = errorCode === RECOVERY_LINK_FAILED_CODE
  const isMagicLinkFailed = errorCode === MAGIC_LINK_FAILED_CODE
  const errorMessage = isRecoveryLinkFailed
    ? t('errors.recoveryLinkFailed')
    : isMagicLinkFailed
      ? t('errors.magicLinkFailed')
      : errorParam
  const initialEmail = searchParams.get('email') || ''
  const flow = searchParams.get('flow') // 'login' or 'signup'
  const nextUrl = searchParams.get('next') // Deep link to return to after login

  // Derive expected mode from props/params
  const derivedMode = useMemo((): 'login' | 'signup' => {
    return (initialFlow === 'signup' || flow === 'signup') && organization ? 'signup' : 'login'
  }, [initialFlow, flow, organization])

  // Track if user has manually overridden the mode
  const [userOverride, setUserOverride] = useState<'login' | 'signup' | null>(null)

  // Reset user override when derivedMode changes via useEffect
  // This is legitimate - syncing state with external URL/props changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing with URL params is valid
    setUserOverride(null)
  }, [derivedMode])

  // Final mode: use user override if set, otherwise use derived
  const mode = userOverride ?? derivedMode

  // Persistent "account already exists" alert. Replaces the ephemeral toast
  // that users were missing entirely (Juan's #1 complaint). Cleared on
  // explicit mode toggle or successful re-submission.
  const [accountFoundEmail, setAccountFoundEmail] = useState<string | null>(null)

  // Force-remount the password input on signup→login auto-switch so the
  // typed signup password doesn't carry over (users would otherwise retry
  // the same wrong string).
  const [passwordKey, setPasswordKey] = useState(0)

  const setMode = (next: 'login' | 'signup' | null) => {
    setUserOverride(next)
    setAccountFoundEmail(null)
  }

  // Invitation context: invitee landed via /join → /login?email=…&flow=signup&org=…
  const isInvitationFlow = Boolean(initialEmail && initialFlow === 'signup' && organization)

  useEffect(() => {
    if (errorMessage) {
      toast.error(t('toast.loginError'), { description: errorMessage })
    }
  }, [errorMessage, t])

  const handleLinkedInLogin = async () => {
    setIsLinkedInLoading(true)
    try {
      // Note: Don't pass the invitation token to LinkedIn OAuth.
      // Regular invitations use the invitation_token cookie (set by /join page),
      // and passing the token would redirect to /join/[token] (admin onboarding page)
      // which rejects non-ORG_ADMIN invitations. The cookie + email auto-matching
      // in the callback handles regular invitations correctly.
      const result = await signInWithLinkedIn(undefined, mode, organization?.slug)

      // If signInWithLinkedIn returned an error (e.g., provider misconfigured, rate limit)
      // the redirect didn't happen, so we need to show the error
      if (result?.error) {
        toast.error(t('toast.linkedInLoginFailed'), { description: result.error })
        setIsLinkedInLoading(false)
      }
      // If no error and no redirect happened (edge case: no data.url returned)
      // reset loading state after a timeout to prevent infinite spinner
      else if (result === undefined) {
        setTimeout(() => {
          setIsLinkedInLoading(false)
          toast.error(t('toast.linkedInNoResponse'), {
            description: t('toast.linkedInNoResponseDescription'),
          })
        }, 5000)
      }
    } catch (err) {
      // On redirect, keep spinner showing until page transitions
      if ((err as { digest?: string })?.digest?.startsWith('NEXT_REDIRECT')) return
      toast.error(t('toast.linkedInLoginFailed'), {
        description: t('toast.linkedInConnectError'),
      })
      setIsLinkedInLoading(false)
    }
  }

  const handleSubmit = async (formData: FormData) => {
    setIsLoading(true)
    try {
      const serverAction = mode === 'login' ? login : signup
      const result = await serverAction(formData)

      if (result?.error) {
        // Auto-switch to login mode if user already has an account.
        // Surfaces as a persistent inline alert (not a toast) so the user
        // can't miss it, and clears the typed signup password to avoid the
        // "I just typed my password, why isn't it working?" loop.
        if ('code' in result && result.code === 'already_registered') {
          const submittedEmail = (formData.get('email')?.toString().trim() ?? '').toLowerCase()
          setAccountFoundEmail(submittedEmail || initialEmail || null)
          setPasswordKey((k) => k + 1)
          setUserOverride('login')
          setIsLoading(false)
          return
        }

        toast.error(t('toast.authenticationFailed'), {
          description: result.error,
        })
        setIsLoading(false)
      } else {
        // If no error, action handled redirect
        if (mode === 'signup') {
          toast.success(t('toast.applicationReceived'), {
            description: t('toast.checkEmailToVerify'),
          })
          setIsLoading(false)
        }
        // For login success, keep loading state - redirect will unmount component
      }
    } catch (e) {
      // On redirect, keep spinner showing until page transitions
      if ((e as { digest?: string })?.digest?.startsWith('NEXT_REDIRECT')) return
      console.error('Login error:', e)
      toast.error(t('toast.somethingWentWrong'), {
        description: t('toast.tryAgainLater'),
      })
      setIsLoading(false)
    }
  }

  const isKeyboardOpen = useKeyboardVisible()
  const currentLocale = useLocale()

  // Reserve space at the bottom of the mobile scroll container so the fixed
  // CTA bar never overlaps the flowing footer (terms + locale switcher). The
  // signup CTA is taller because it carries an extra consent line, so it needs
  // more clearance. `max(1.5rem, safe-area)` mirrors the bar's own bottom
  // padding so the gap above the bar stays constant across devices with and
  // without a home indicator. Reset to md:p-8 on desktop where the bar is static.
  const mobileCtaClearance =
    mode === 'signup'
      ? 'pb-[calc(11.5rem+max(1.5rem,env(safe-area-inset-bottom)))]'
      : 'pb-[calc(8rem+max(1.5rem,env(safe-area-inset-bottom)))]'

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white md:bg-[#fdfbf7] md:p-4">
      <div
        className={`w-full flex flex-col flex-1 md:flex-none max-w-md bg-white md:border md:border-border/50 md:shadow-sm md:rounded-md px-6 pt-[calc(env(safe-area-inset-top)+2rem)] md:pt-8 ${mobileCtaClearance} md:p-8 animate-in fade-in zoom-in-95 duration-300 relative`}
      >
        <div className="text-center mb-6 md:mb-8">
          {organization ? (
            <div className="flex flex-col items-center gap-3">
              {organization.logo_url && (
                <div className="w-16 h-16 md:w-20 md:h-20 mb-1">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={organization.logo_url}
                    alt={organization.name}
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
              <h1 className="font-serif text-2xl md:text-3xl font-bold text-primary">
                {organization.name}
              </h1>
              <p className="text-muted-foreground">
                {mode === 'signup' ? t('applyForMembership') : t('memberLogin')}
              </p>
            </div>
          ) : (
            <>
              <h1 className="font-serif text-4xl font-bold text-primary mb-2">Alumnia</h1>
              <p className="text-muted-foreground">{t('tagline')}</p>
            </>
          )}

          {/* Referral banner */}
          {referralCode && organization && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
              <span className="font-medium">
                {referrerName ? t('invitedByName', { name: referrerName }) : t('invitedByMember')}
              </span>
              <br />
              <span className="text-green-600">{t('signUpToJoin')}</span>
            </div>
          )}

          {/* Invitation banner: shown when invitee landed via /join → /login
              with email + signup flow + org context. Persistent (not a toast)
              so the user understands they're on the right page. */}
          {isInvitationFlow && organization && mode === 'signup' && !accountFoundEmail && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 text-left">
              <p className="font-medium">
                {t('invitationBanner.title', { orgName: organization.name })}
              </p>
              <p className="text-blue-700 mt-1">{t('invitationBanner.description')}</p>
            </div>
          )}

          {/* Persistent "account already exists" alert. Replaces the ephemeral
              toast that auto-dismissed before users could read it. Stays
              visible until the user successfully signs in or toggles mode. */}
          {accountFoundEmail && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 text-left">
              <p className="font-medium">
                {t('alreadyRegistered.title', { email: accountFoundEmail })}
              </p>
              <p className="text-amber-800 mt-1">
                {organization
                  ? t('alreadyRegistered.description', { orgName: organization.name })
                  : t('alreadyRegistered.descriptionGeneric')}
              </p>
              <Link
                href={`/forgot-password?email=${encodeURIComponent(accountFoundEmail)}`}
                className="inline-block mt-2 text-sm font-medium text-amber-900 underline hover:text-amber-950"
              >
                {t('alreadyRegistered.forgotPassword')}
              </Link>
            </div>
          )}

          {/* Persistent error banner. Recovery-link failures get an actionable
              CTA prefilled with the user's email (if known) so a fresh link is
              one click away — closing the dead-end UX that silently dropped
              ~76% of recovery attempts pre-token-hash. */}
          {errorMessage && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              <p className="font-medium">{t('signInFailed')}</p>
              <p className="text-red-600 mt-1">{errorMessage}</p>
              {isRecoveryLinkFailed ? (
                <Link
                  href={`/forgot-password${initialEmail ? `?email=${encodeURIComponent(initialEmail)}` : ''}`}
                  className="inline-block mt-3 text-sm font-medium text-red-700 underline hover:text-red-900"
                >
                  {t('actions.sendFreshResetLink')}
                </Link>
              ) : isMagicLinkFailed ? (
                <Link
                  href="/start"
                  className="inline-block mt-3 text-sm font-medium text-red-700 underline hover:text-red-900"
                >
                  {t('actions.requestNewLink')}
                </Link>
              ) : (
                <p className="text-xs text-red-500 mt-2">{t('tryEmailPasswordInstead')}</p>
              )}
            </div>
          )}
        </div>

        <div className="space-y-4">
          {organization && (
            <>
              <Button
                type="button"
                onClick={handleLinkedInLogin}
                disabled={isLoading || isLinkedInLoading}
                className="w-full bg-[#0077b5] hover:bg-[#006396] text-white font-bold py-5 h-12 md:h-10 text-base md:text-sm"
              >
                {isLinkedInLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    {mode === 'signup' ? t('connecting') : t('signingIn')}
                  </>
                ) : (
                  <>
                    <Linkedin className="w-5 h-5 mr-2 fill-current" />
                    {mode === 'signup' ? t('signUpWithLinkedIn') : t('signInWithLinkedIn')}
                  </>
                )}
              </Button>

              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border/60" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-muted-foreground">{t('orWithEmail')}</span>
                </div>
              </div>
            </>
          )}
        </div>

        <form
          className="space-y-5 md:space-y-4 mt-4"
          onSubmit={(e) => {
            e.preventDefault()
            handleSubmit(new FormData(e.currentTarget))
          }}
        >
          {organization && <input type="hidden" name="orgSlug" value={organization.slug} />}
          {nextUrl && <input type="hidden" name="next" value={nextUrl} />}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-foreground mb-1.5 md:mb-1"
            >
              {t('emailAddress')}
            </label>
            <Input
              id="email"
              name="email"
              type="email"
              defaultValue={initialEmail}
              autoComplete="email"
              required
              placeholder="colleague@university.edu"
              className={`bg-white md:bg-[#fdfbf7] h-12 md:h-10 text-base md:text-sm ${
                isInvitationFlow && mode === 'signup' ? 'bg-muted/30 text-muted-foreground' : ''
              }`}
              inputMode="email"
              enterKeyHint="next"
              disabled={isLoading}
              readOnly={isInvitationFlow && mode === 'signup'}
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-1.5 md:mb-1">
              <label htmlFor="password" className="block text-sm font-medium text-foreground">
                {mode === 'signup' ? t('createPassword') : t('password')}
              </label>
              {mode === 'login' && (
                <Link
                  href="/forgot-password"
                  className="text-xs text-muted-foreground hover:text-primary hover:underline"
                >
                  {t('forgotPassword')}
                </Link>
              )}
            </div>
            <PasswordInput
              key={passwordKey}
              id="password"
              name="password"
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              required
              className="bg-white md:bg-[#fdfbf7] h-12 md:h-10 text-base md:text-sm"
              enterKeyHint="done"
              disabled={isLoading}
            />
          </div>

          {/* Sticky bottom CTA on mobile */}
          <div
            className={`fixed bottom-0 left-0 right-0 md:static md:bottom-auto md:left-auto md:right-auto bg-white md:bg-transparent border-t border-border/50 md:border-t-0 p-4 md:p-0 md:pb-0 z-10 transition-transform duration-200 ${isKeyboardOpen ? 'translate-y-full md:translate-y-0' : 'translate-y-0'}`}
            style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
          >
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full font-bold text-base h-14 md:h-10 py-0"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {mode === 'signup' ? t('creatingAccount') : t('loggingIn')}
                </>
              ) : mode === 'signup' ? (
                t('completeRegistration')
              ) : (
                t('logIn')
              )}
            </Button>

            {/* Sign-in-wrap consent notice, placed immediately adjacent to the
                primary action so assent is conspicuous (no explicit checkbox).
                Login mode keeps the equivalent notice in the card footer. */}
            {mode === 'signup' && (
              <p className="text-center text-xs text-muted-foreground leading-relaxed mt-3 px-2">
                {t('byContinuingPrefix')}{' '}
                <Link
                  href="/policies/terms"
                  target="_blank"
                  className="underline hover:text-primary"
                >
                  {t('termsOfService')}
                </Link>{' '}
                {t('and')}{' '}
                <Link
                  href="/policies/privacy"
                  target="_blank"
                  className="underline hover:text-primary"
                >
                  {t('privacyPolicy')}
                </Link>
                .
              </p>
            )}

            <div className="text-center mt-5 md:mt-2">
              {mode === 'login' ? (
                organization ? (
                  <p className="text-sm text-muted-foreground">
                    {t('dontHaveAccount')}{' '}
                    <button
                      type="button"
                      onClick={() => setMode('signup')}
                      className="text-primary font-semibold hover:underline"
                    >
                      {t('applyForMembership')}
                    </button>
                  </p>
                ) : null
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t('alreadyHaveAccount')}{' '}
                  <button
                    type="button"
                    onClick={() => setMode('login')}
                    className="text-primary font-semibold hover:underline"
                  >
                    {t('logIn')}
                  </button>
                </p>
              )}
            </div>
          </div>
        </form>

        {/* Push the footer to the bottom of the scroll area on mobile so it
            sits directly above the fixed CTA bar instead of colliding with it. */}
        <div className="flex-1 md:hidden" aria-hidden="true" />

        <div className="mt-8 mb-6 md:mb-0 text-center text-sm text-muted-foreground pb-safe md:pb-0">
          {/* Consent fine print. In signup mode this lives directly under the
              CTA (see above) to maximize conspicuousness; here it serves login. */}
          {mode !== 'signup' && (
            <p>
              {t('byContinuingPrefix')}{' '}
              <Link
                href="/policies/terms"
                target="_blank"
                className="hover:text-primary hover:underline"
              >
                {t('termsOfService')}
              </Link>{' '}
              {t('and')}{' '}
              <Link
                href="/policies/privacy"
                target="_blank"
                className="hover:text-primary hover:underline"
              >
                {t('privacyPolicy')}
              </Link>
              .
            </p>
          )}
          <div className="mt-3 flex justify-center">
            <LocaleSwitcher currentLocale={currentLocale} />
          </div>
        </div>
      </div>
    </div>
  )
}
